import { useEffect, useRef } from 'react';
import { type Location, useBlocker } from 'react-router';
import { trackError } from 'utils/trackError';

const interactionIdOf = (location: Location) =>
  (location.state as { interactionId?: string } | null)?.interactionId;

export function useRecordingLeaveGuard(
  isActive: boolean,
  onLeaveWhileActive: () => void | Promise<void>
): void {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!isActive) return false;
    return (
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search ||
      currentLocation.hash !== nextLocation.hash ||
      interactionIdOf(currentLocation) !== interactionIdOf(nextLocation)
    );
  });

  // In-app navigation is non-destructive (it just pauses), so resolve any blocked
  // navigation automatically instead of asking the user.
  // settledRef guards against calling proceed() twice for the same block: the
  // callback below can change recordingPhase, which changes isActive/
  // onLeaveWhileActive identity and re-fires this effect while blocker.state
  // still reads 'blocked' (the router hasn't flushed to 'proceeding' yet).
  const settledRef = useRef(true);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      settledRef.current = true;
      return;
    }
    if (!settledRef.current) return;
    settledRef.current = false;
    const leave = async () => {
      try {
        if (isActive) await onLeaveWhileActive();
      } catch (error) {
        // Leave anyway: the backend also stores the pause when the socket closes.
        trackError(error, { context: 'recordingLeaveGuard' });
      }
      blocker.proceed();
    };
    void leave();
  }, [blocker, isActive, onLeaveWhileActive]);

  useEffect(() => {
    if (!isActive) return;

    // Unlike in-app navigation, a tab close/reload can't reliably run and
    // await onLeaveWhileActive, so this still asks the browser to confirm.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required by Chrome to surface the native confirmation dialog.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isActive]);
}
